import {Button, FormControl, InputLabel, MenuItem, Select, Typography} from '@material-ui/core';
import classNames from 'classnames';
import {Async, Loaded, matchAsync} from 'Core/models/Async';
import {constant} from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';
import {fold as foldOption, fromNullable, isNone, some} from 'fp-ts/lib/Option';
import React, {ReactNode, useState} from 'react';
import Dropzone from 'react-dropzone';
import {useDispatch, useSelector} from 'react-redux';
import ErrorPage from 'Shared/components/ErrorPage';
import Loading from 'Shared/components/Loading';
import Modal from 'Shared/components/Modal';
import {hasScope} from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {UserUploadFileTypeId} from 'Shared/constants/UserUploadFileType';
import {copyToClipboard} from 'Shared/utilities/CopyToClipboard';
import {uploadUsers} from 'Users/ducks/userAdministration';
import {
  CompletedFileUpload,
  EmptyUserFile,
  FileWithUnsupportedFormat,
  InvalidUserFile,
  matchUserFile,
  UserFile,
  UserFileResults,
  ValidUserFile,
} from 'Users/models/UserFile';
import agencyFilterSelector from 'Users/selectors/selectAgencyFilters';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './Registration.sass';

type RegistrationProps = {
  onClose: () => any;
};

export const Registration = (props: RegistrationProps) => {
  const dispatch = useDispatch();

  const [fileToUpload, changeFileToUpload] = useState<UserFile>(new EmptyUserFile());
  const scopes = useSelector(selectUserScopes);
  const userHasScope = hasScope(scopes);
  const agencyFilters = useSelector(agencyFilterSelector);

  const renderFileDropzone = (file: FileWithUnsupportedFormat | ValidUserFile | InvalidUserFile | EmptyUserFile) => {
    return (
      <>
        {(file instanceof InvalidUserFile && isNone(file.file)) ||
        file instanceof EmptyUserFile ||
        file instanceof FileWithUnsupportedFormat ? (
          <>
            <Typography>Upload an Excel file below to bulk import new users.</Typography>
            <Dropzone
              onDrop={files => changeFileToUpload(ValidUserFile.create(fromNullable(files[0]), file.fileTypeId))}
            >
              {({getRootProps, getInputProps, isDragActive}) => {
                return (
                  <div
                    {...getRootProps()}
                    className={classNames('registration__file-dropzone', {
                      'registration__file-dropzone--active': isDragActive,
                    })}
                  >
                    <i className="registration__file-watermark fa fa-4x fa-file-text" />
                    {isDragActive ? 'Drop File Here' : 'Drop File Here Or Click To Browse'}
                    <input {...getInputProps()} type="file" hidden accept=".xlsx" />
                  </div>
                );
              }}
            </Dropzone>
            <Typography className="registration__supported-file-message" variant="caption">
              * Supported File Format(s): .xlsx
            </Typography>
            {file instanceof FileWithUnsupportedFormat && (
              <Typography className="registration__invalid-file-format-message">{file.errorMessage}</Typography>
            )}
          </>
        ) : (
          <div className="registration__file-container">
            <i className="registration__file-icon fa fa-2x fa-file-text" />
            {file instanceof ValidUserFile
              ? file.file.name
              : foldOption<File, string>(constant(''), f => f.name)(file.file)}
          </div>
        )}
        <FormControl className="registration__file-upload-type" fullWidth>
          <InputLabel htmlFor="file-upload-type">User Type</InputLabel>
          <Select
            value={
              file instanceof ValidUserFile ? file.fileTypeId : foldOption(constant(0), value => value)(file.fileTypeId)
            }
            onChange={e =>
              changeFileToUpload(
                ValidUserFile.create(
                  file instanceof ValidUserFile ? some(file.file) : file.file,
                  fromNullable(e.target.value as UserUploadFileTypeId)
                )
              )
            }
            inputProps={{
              name: 'file-upload-type',
              id: 'file-upload-type',
            }}
          >
            {(userHasScope(Scope.UploadIndependentAgents) || userHasScope(Scope.UploadIndependentAgentsByAgency)) && (
              <MenuItem value={UserUploadFileTypeId.IndependentAgent}>Independent Agent</MenuItem>
            )}
            {userHasScope(Scope.UploadVips) && <MenuItem value={UserUploadFileTypeId.VIP}>VIP</MenuItem>}
            {userHasScope(Scope.UploadExclusiveAgents) && (
              <MenuItem value={UserUploadFileTypeId.ExclusiveAgent}>Exclusive Agent</MenuItem>
            )}
            {userHasScope(Scope.UploadFieldSalesLeaders) && (
              <MenuItem value={UserUploadFileTypeId.FieldLeader}>Field Leader</MenuItem>
            )}
            {userHasScope(Scope.UploadFieldVicePresidents) && (
              <MenuItem value={UserUploadFileTypeId.FieldVicePresident}>Field Vice President</MenuItem>
            )}
            {userHasScope(Scope.UploadEnrollTrackUser) && (
              <MenuItem value={UserUploadFileTypeId.EnrollTrackUser}>EnrollTrack User</MenuItem>
            )}
          </Select>
        </FormControl>
        <Button
          className="registration__save-button"
          variant="contained"
          color="primary"
          disabled={!(file instanceof ValidUserFile)}
          onClick={() => {
            if (file instanceof ValidUserFile) {
              dispatch(
                uploadUsers(
                  file,
                  (asyncResults: Async<UserFileResults>) => {
                    changeFileToUpload(new CompletedFileUpload(asyncResults));
                  },
                  O.toUndefined(agencyFilters)
                )
              );
            }
          }}
          fullWidth
        >
          Create Accounts
        </Button>
      </>
    );
  };

  const renderFileUploadCompleted = (completedUpload: UserFileResults) => {
    return (
      <div>
        <Typography>Successful Imports: {completedUpload.successCount}</Typography>
        <Typography>Failed Imports: {completedUpload.failureCount}</Typography>
        {completedUpload.errors.length > 0 && (
          <div className="registration__error-messages">
            Errors:
            <div className="registration__errors-container">
              {completedUpload.errors.map(error => (
                <p>{error}</p>
              ))}
            </div>
          </div>
        )}
        <div className="registration__actions">
          <Button
            className="registration__copy-button"
            variant="contained"
            fullWidth
            onClick={e => {
              copyToClipboard(completedUpload.errors.join('\n'))();
            }}
          >
            Copy Errors
          </Button>
          <Button
            className="registration__save-button"
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => props.onClose()}
          >
            Close
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      disableBackdropClick
      title={
        fileToUpload instanceof CompletedFileUpload && fileToUpload instanceof Loaded
          ? 'Import Complete'
          : 'Upload Users'
      }
      open
      onClose={props.onClose}
    >
      <div className="registration__modal-body">
        {matchUserFile<ReactNode>(fileToUpload, {
          EmptyUserFile: renderFileDropzone,
          InvalidUserFile: renderFileDropzone,
          FileWithUnsupportedFormat: renderFileDropzone,
          ValidUserFile: renderFileDropzone,
          CompletedUserFile: file =>
            matchAsync(file.results, {
              Loaded: renderFileUploadCompleted,
              Loading: () => <Loading />,
              ServerError: error => <ErrorPage error={error} />,
              Unauthorized: error => <ErrorPage error={error} />,
              Conflict: error => <ErrorPage error={error} />,
            }),
        })}
      </div>
    </Modal>
  );
};

export default Registration;
